import { useState, useCallback, useEffect, useRef } from 'react'
import { Box, Button, Typography, Link, SxProps, Theme } from '@mui/material'
import { PollingFlowStatus } from './PollingFlowStatus'
import {
  ContentServiceFlowResults,
  ContentServiceFlowStatus,
  RequestError,
} from './types'
import {
  getFlowResults,
  isRequestError,
  isUnauthorizedResponse,
  isPermanentError,
} from './utility'
import { openInNewTab } from 'shared/utils/fileExport'

interface StartAndMonitorFlowProps {
  disabled: boolean
  buttonText: string
  processingText: string
  startFlowFunction: () => Promise<string>
  onFlowComplete?: (flowResults: ContentServiceFlowResults) => void
  onFlowPoll?: (
    flowStatus: ContentServiceFlowStatus | RequestError | undefined,
  ) => void
  pollingIntervalMs?: number
  downloadResults?: boolean
  resetTrigger?: number
  buttonSx?: SxProps<Theme>
  buttonSize?: 'small' | 'medium' | 'large'
}

export function StartAndMonitorFlow({
  disabled,
  buttonText,
  processingText,
  startFlowFunction,
  onFlowComplete,
  onFlowPoll: onFlowPollProp,
  pollingIntervalMs = 1000,
  downloadResults = false,
  resetTrigger,
  buttonSx,
  buttonSize,
}: StartAndMonitorFlowProps) {
  const [flowId, setFlowId] = useState<string>()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isFlowCompleted, setIsFlowCompleted] = useState(false)
  const [hasPermanentError, setHasPermanentError] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [flowResults, setFlowResults] = useState<ContentServiceFlowResults>()

  // Use ref so the external callback is always fresh inside onFlowPoll
  // without adding it as a useCallback dependency (which would destabilize the
  // callback and cause PollingFlowStatus's useEffect to re-fire)
  const onFlowPollPropRef = useRef(onFlowPollProp)
  onFlowPollPropRef.current = onFlowPollProp

  // Reset all state when resetTrigger is 0 (indicating a reset)
  // This ensures the component returns to its initial state
  useEffect(() => {
    if (resetTrigger === 0) {
      setFlowId(undefined)
      setIsProcessing(false)
      setIsFlowCompleted(false)
      setHasPermanentError(false)
      setError(null)
      setFlowResults(undefined)
    }
  }, [resetTrigger])

  const onFlowPoll = useCallback(
    (flowStatus: ContentServiceFlowStatus | RequestError | undefined) => {
      onFlowPollPropRef.current?.(flowStatus)
      if (!flowStatus) return
      if (isRequestError(flowStatus)) {
        // 401 and 404 errors are permanent - stop processing and disable button
        if (isPermanentError(flowStatus)) {
          setIsProcessing(false)
          setHasPermanentError(true)
          setError(
            isUnauthorizedResponse(flowStatus)
              ? `Unauthorized: ${flowStatus.errorText}`
              : `Flow not found: ${flowStatus.errorText}`,
          )
          return
        }
        // Other errors are temporary - show error but continue polling
        setError(`Error checking flow status: ${flowStatus.errorText}`)
        // Don't stop processing on temporary errors
        return
      }
      // Clear error on successful poll (unless it's a permanent error)
      if (!hasPermanentError) {
        setError(null)
      }
      if (flowId && flowStatus.data.completed) {
        setIsProcessing(false)
        setIsFlowCompleted(true)
        getFlowResults(flowId).then((results) => {
          if (isRequestError(results)) {
            setError(`Error fetching flow results: ${results.errorText}`)
            return
          }
          const hasFailures =
            !!results.data.status.dependant_jobs.failed ||
            !!results.data.status.worker_jobs.failed ||
            !!results.errors?.length
          if (downloadResults || hasFailures) {
            setFlowResults(results)
          }
          onFlowComplete?.(results)
        })
      }
    },
    [downloadResults, flowId, onFlowComplete, hasPermanentError],
  )

  const handleStartFlow = useCallback(async () => {
    try {
      setIsProcessing(true)
      setIsFlowCompleted(false)
      setHasPermanentError(false)
      setError(null)
      setFlowId(undefined) // Clear previous flowId
      setFlowResults(undefined) // Clear previous results

      const newFlowId = await startFlowFunction()
      setFlowId(newFlowId)
    } catch (err) {
      setIsProcessing(false)
      const errorMessage = err instanceof Error ? err.message : String(err)
      // Check if it's a 401 error
      const errorObj: RequestError = {
        requestError: true,
        errorText: errorMessage,
      }
      if (isUnauthorizedResponse(errorObj)) {
        setHasPermanentError(true)
      }
      setError(`Failed to start flow: ${errorMessage}`)
    }
  }, [startFlowFunction])

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button
          disabled={
            disabled ||
            isProcessing ||
            !!flowId ||
            isFlowCompleted ||
            hasPermanentError
          }
          onClick={handleStartFlow}
          variant="contained"
          size={buttonSize}
          sx={buttonSx}
        >
          {isProcessing ? processingText : buttonText}
        </Button>
      </Box>
      {error && (
        <Typography variant="body2" color="error" mt={1}>
          {error}
        </Typography>
      )}
      <PollingFlowStatus
        key={`${resetTrigger}-${flowId || 'no-flow'}`}
        flowId={flowId}
        onFlowPoll={onFlowPoll}
        pollingIntervalMs={pollingIntervalMs}
        additionalHeaderItems={<DownloadLink flowResults={flowResults} />}
      />
    </Box>
  )
}

function DownloadLink({
  flowResults,
}: {
  flowResults?: ContentServiceFlowResults
}) {
  if (!flowResults) return null

  const handleOpenResults = () => {
    if (!flowResults) return
    openInNewTab({ data: flowResults })
  }

  const handleDownloadResults = () => {
    if (!flowResults) return

    const dataStr = JSON.stringify(flowResults, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'results.json'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <Link
        component="button"
        variant="body2"
        onClick={handleOpenResults}
        sx={{ cursor: 'pointer', ml: 1 }}
      >
        Open Results (JSON)
      </Link>
      <Link
        component="button"
        variant="body2"
        onClick={handleDownloadResults}
        sx={{ cursor: 'pointer', ml: 1 }}
      >
        Download Results (JSON)
      </Link>
    </>
  )
}
